import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, TrendingDown, Loader2 } from "lucide-react";

// Plan Conversion tile — shows on the manager dashboard.
// Answers: "of every 10 plan quotes we sent, how many landed a live plan?"
// Plus a per-advisor leaderboard so Steve can see who's actually selling.
export default function PlanConversionTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/analytics/service-plan-conversion?days=30")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card-default p-5 flex items-center justify-center min-h-[140px]" data-testid="plan-conversion-tile-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data || data.sent === 0) {
    return (
      <div className="card-default p-5" data-testid="plan-conversion-tile-empty">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/15 text-warning grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-warning">Service plan conversion · 30d</div>
            <div className="font-display font-bold text-base mt-0.5">No plan quotes sent yet</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              When a diary import spots a car with no live plan, hit the "Plan opportunity"
              button on the pre-call row to send a quote — conversion will start tracking here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const delta = data.delta_pct || 0;
  const deltaColor = delta > 0 ? "text-success" : delta < 0 ? "text-destructive" : "text-muted-foreground";
  const DeltaIcon = delta >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="card-default p-5" data-testid="plan-conversion-tile">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-warning/15 text-warning grid place-items-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-overline text-warning">Service plan conversion · last 30d</div>
          <div className="flex items-baseline gap-3 mt-1 flex-wrap">
            <div className="font-display font-bold text-3xl tracking-tight" data-testid="plan-conversion-rate">
              {data.conversion_pct}%
            </div>
            {data.previous.sent > 0 && (
              <div className={`inline-flex items-center gap-1 text-xs font-bold ${deltaColor}`}>
                <DeltaIcon className="w-3 h-3" />
                {delta > 0 ? "+" : ""}{delta}% vs previous 30d
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <span className="font-bold text-foreground">{data.converted}</span> converted from{" "}
            <span className="font-bold text-foreground">{data.sent}</span> quotes sent · locking in{" "}
            <span className="font-bold text-foreground font-mono-plex">£{data.total_monthly_won.toFixed(2)}</span> new monthly revenue
          </div>
        </div>
      </div>

      {data.by_advisor?.length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <div className="label-overline text-muted-foreground text-[10px] mb-2">Leaderboard</div>
          <ul className="space-y-1.5" data-testid="plan-conversion-leaderboard">
            {data.by_advisor.slice(0, 6).map((row) => (
              <li key={row.advisor_email} className="flex items-center gap-2 text-xs" data-testid={`plan-lb-${row.advisor_email}`}>
                <div className="flex-1 min-w-0 truncate font-medium">{row.advisor_name}</div>
                <div className="text-muted-foreground">
                  {row.converted}/{row.sent}
                </div>
                <div className={`font-bold w-12 text-right ${
                  row.conversion_pct >= 60 ? "text-success"
                  : row.conversion_pct >= 30 ? "text-warning"
                  : "text-muted-foreground"
                }`}>
                  {row.conversion_pct}%
                </div>
                <div className="hidden sm:block w-20 text-right font-mono-plex text-muted-foreground">
                  £{row.total_monthly_won.toFixed(0)}/mo
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
